// Import package components
import { useContext, useEffect, useState } from "react";
import UIkit from "uikit";

// Import base components
import { AppContexts } from "../../../providers";

// Import common components
import Dialog from "../../common/Dialog";
import TaskOccurrencesService from "../../../services/taskOccurrences.service";
import {
  isRecurringWorkOrder,
  RECURRING_SCOPE_LABELS,
} from "../recurringScopePrompt";

const WorkOrderDelete = ({
  itemLabel = "Task",
  onDeleted,
  closeEditOnDelete = false,
}) => {
  const workOrderContext = useContext(AppContexts.WorkOrderContext);

  const { workOrder, loading } = workOrderContext;
  const recurring = isRecurringWorkOrder(workOrder);
  const [deleteScope, setDeleteScope] = useState("this");

  useEffect(() => {
    setDeleteScope("this");
  }, [workOrder?.id]);

  const closeEditPanel = () => {
    if (!closeEditOnDelete) return;
    try {
      UIkit.offcanvas("#work-order-add")?.hide?.();
    } catch (_) {
      /* ignore */
    }
    workOrderContext.initWorkOrder?.();
  };

  const handleDelete = async () => {
    try {
      if (workOrder?.isVirtual) {
        if (recurring && deleteScope === "future") {
          await TaskOccurrencesService.deleteFuture({
            rule_id: workOrder.task_recurrence_rule_id,
            from_date: String(workOrder.scheduled_date || "").slice(0, 10),
          });
        } else if (recurring && deleteScope === "all") {
          await TaskOccurrencesService.deleteAll({
            rule_id: workOrder.task_recurrence_rule_id,
          });
        } else {
          await TaskOccurrencesService.skip({
            rule_id: workOrder.task_recurrence_rule_id,
            occurrence_key: workOrder.occurrence_key,
          });
        }
      } else {
        await workOrderContext.deleteWorkOrder(workOrder.id, {
          delete_scope: recurring ? deleteScope : "this",
        });
      }
      UIkit.modal("#work-order-delete").hide();
      closeEditPanel();
      await onDeleted?.();
      UIkit.notification(
        deleteScope === "future" && recurring
          ? "This and all future occurrences deleted"
          : deleteScope === "all" && recurring
            ? "All upcoming occurrences deleted"
            : `${itemLabel} deleted`,
        "success"
      );
    } catch (_) {
      UIkit.notification(`Failed to delete ${itemLabel.toLowerCase()}`, "danger");
    }
  };

  return (
    <>
      <Dialog id="work-order-delete">
        <div className="uk-modal-dialog uk-modal-body">
          <button
            className="uk-modal-close-default"
            type="button"
            uk-close="true"
          ></button>
          <h2 className="uk-modal-title">Delete {itemLabel}</h2>
          <p>
            Are you sure you want to delete <b>{workOrder?.summary}</b>?
          </p>
          {recurring && (
            <div className="uk-margin">
              <p className="uk-text-meta uk-margin-small-bottom">
                {RECURRING_SCOPE_LABELS.metaDelete}
              </p>
              <label className="uk-display-block uk-margin-small">
                <input
                  className="uk-radio uk-margin-small-right"
                  type="radio"
                  name="work-order-delete-scope"
                  value="this"
                  checked={deleteScope === "this"}
                  onChange={() => setDeleteScope("this")}
                />
                {RECURRING_SCOPE_LABELS.this}
              </label>
              <label className="uk-display-block uk-margin-small">
                <input
                  className="uk-radio uk-margin-small-right"
                  type="radio"
                  name="work-order-delete-scope"
                  value="future"
                  checked={deleteScope === "future"}
                  onChange={() => setDeleteScope("future")}
                />
                {RECURRING_SCOPE_LABELS.future}
              </label>
              <label className="uk-display-block">
                <input
                  className="uk-radio uk-margin-small-right"
                  type="radio"
                  name="work-order-delete-scope"
                  value="all"
                  checked={deleteScope === "all"}
                  onChange={() => setDeleteScope("all")}
                />
                {RECURRING_SCOPE_LABELS.all}
              </label>
              <p className="uk-text-meta uk-margin-small-top">
                {RECURRING_SCOPE_LABELS.allNote}
              </p>
            </div>
          )}
          <p className="uk-text-right">
            <button
              className="uk-button hms-btn uk-modal-close uk-modal-delete-cancel"
              type="button"
            >
              Cancel
            </button>
            <button
              className="uk-button hms-btn hms-gray-btn"
              type="button"
              onClick={handleDelete}
            >
              {loading === true ? (
                <div data-uk-spinner="ratio: 0.5;"></div>
              ) : (
                "Delete"
              )}
            </button>
          </p>
        </div>
      </Dialog>
    </>
  );
};

export default WorkOrderDelete;
